import { LegalPage } from "@/components/LegalPage";

const UPDATED = "15 October 2026";

const TERMS_SECTIONS = [
  {
    heading: "1. About These Terms",
    p: [
      "These Terms and Conditions govern your access to and use of Get Set Design, including its website, tools and image-processing services (the “Service”).",
      "In these Terms, “Get Set Design”, “we”, “us” or “our” refers to the business operating the Service. “You” or “your” refers to any person or organisation accessing or using the Service.",
      "By accessing or using Get Set Design, you agree to these Terms. If you do not agree, you must not use the Service.",
    ],
  },
  {
    heading: "2. What Get Set Design Does",
    p: [
      "Get Set Design provides tools designed to help users improve the quality, clarity, resolution or usability of digital images and logos. Depending on the features available, the Service may allow you to upload images, process or enhance them, preview results and download processed files.",
      "We may add, modify, suspend or remove features from time to time.",
      "Image processing results depend on the quality, format, resolution and characteristics of the original file. We do not guarantee that every image can be perfectly restored, enhanced or converted into a true vector file.",
    ],
  },
  {
    heading: "3. Eligibility and Acceptable Use",
    p: [
      "You agree to use the Service lawfully and responsibly.",
      "You must not:",
      "We may restrict or suspend access where we reasonably believe these Terms have been breached.",
    ],
    listAfter: 1,
    list: [
      "Upload content that you do not have the right or permission to use.",
      "Infringe another person’s copyright, trade mark, privacy or other intellectual property rights.",
      "Upload unlawful, defamatory, fraudulent or otherwise prohibited material.",
      "Use the Service to create misleading, counterfeit or infringing versions of another person’s branding.",
      "Attempt to interfere with, disrupt, reverse engineer or gain unauthorised access to the Service, except where applicable law permits such activity.",
      "Circumvent usage limits, payment requirements or technical restrictions.",
      "Use automated methods to access or exploit the Service in a manner that damages its operation or breaches these Terms.",
    ],
  },
  {
    heading: "4. Your Uploaded Images and Intellectual Property",
    p: [
      "You retain ownership of the images, logos and other content you upload, subject to any third-party rights in that content.",
      "By uploading content, you confirm that you own it or have all necessary rights, permissions and licences to upload and process it using the Service.",
      "You grant us a limited, non-exclusive licence to host, access, reproduce and process your uploaded content only as reasonably necessary to operate, maintain, secure and provide the Service to you.",
      "This licence does not transfer ownership of your content to us.",
      "We will not claim ownership of your original logo or uploaded artwork merely because you use Get Set Design.",
      "You are responsible for ensuring that your use of any processed output complies with applicable copyright, trade mark and other laws.",
    ],
  },
  {
    heading: "5. Processed Images and Downloads",
    p: [
      "Subject to any third-party rights and applicable law, you may use the processed output for lawful personal or commercial purposes.",
      "You are responsible for checking the final output before using it in print, advertising, websites, merchandise, signage or other commercial applications.",
      "Image enhancement may introduce visual changes, inaccuracies, artefacts or other imperfections. A higher-resolution image is not necessarily a genuine vector graphic.",
      "Unless a feature expressly states otherwise, we do not guarantee that a processed file will be suitable for every intended application, printing specification or professional production workflow.",
      "You should keep your own copies of original files and important downloaded results.",
    ],
  },
  {
    heading: "6. Image Complexity and Accuracy",
    p: [
      "The accuracy and quality of the processed output may vary depending on the complexity, detail and condition of the original logo or image.",
      "Logos containing fine details, intricate shapes, small text, gradients, multiple colours or low-resolution elements may not be reproduced accurately. While we aim to provide the best possible result, we cannot guarantee an exact match to the original artwork. We recommend reviewing all processed outputs carefully before using them for professional, commercial or print purposes.",
    ],
  },
  {
    heading: "7. Upload Storage and File Retention",
    p: [
      "We may temporarily store uploaded images and processed outputs to provide the Service, enable downloads, maintain security and troubleshoot technical issues.",
      "The actual storage and deletion arrangements must be confirmed before publication. We will describe our file retention practices in our Privacy Policy and any relevant notices presented within the Service.",
      "Unless we expressly offer permanent storage or backup, you should not rely on Get Set Design as your sole storage location.",
      "We do not claim a right to use your uploaded artwork for unrelated purposes, advertising, public display or artificial intelligence model training merely because you upload it.",
      "Any additional use of your content must have an appropriate legal basis and, where required, your consent.",
    ],
  },
  {
    heading: "8. Accounts and Security",
    p: [
      "If the Service requires an account, you are responsible for providing accurate information, protecting your login credentials and maintaining the security of your account.",
      "You must notify us promptly if you become aware of unauthorised access to your account.",
      "We may suspend or restrict accounts where reasonably necessary to protect users, the Service or our legal rights.",
    ],
  },
  {
    heading: "9. Pricing and Payments",
    p: [
      "Some features may be offered free of charge, while others may require payment or a subscription.",
      "Any applicable prices, usage limits, billing frequency, included features and renewal conditions will be displayed before you commit to a purchase.",
      "If subscriptions are offered, you are responsible for cancelling them in accordance with the cancellation instructions provided. Cancellation will not affect rights or obligations that have already accrued.",
      "We may change future pricing or introduce paid features. Where required, we will provide appropriate notice before changes affect an existing paid arrangement.",
      "Payment processing may be provided by third-party payment providers and may be subject to their terms.",
    ],
  },
  {
    heading: "10. Refunds and Consumer Rights",
    p: [
      "If you purchase a paid service, our approach to refunds will depend on the circumstances, the service purchased and applicable law.",
      "Nothing in these Terms excludes, restricts or modifies any consumer guarantee, right or remedy that cannot lawfully be excluded under the Australian Consumer Law or other applicable legislation.",
      "Where the Service fails to meet a consumer guarantee, you may be entitled to a remedy, including a refund or compensation, depending on the circumstances.",
      "For billing issues, failed transactions or problems with a purchased feature, contact us using the details provided on our website.",
    ],
  },
  {
    heading: "11. Service Availability and Technical Limitations",
    p: [
      "We aim to provide a reliable service but do not guarantee that the Service will always be available, uninterrupted, error-free or compatible with every device, browser or file format.",
      "Processing times may vary depending on file size, image complexity, system demand and the processing technology used.",
      "We may carry out maintenance, introduce updates or temporarily suspend features when reasonably necessary.",
      "We will take reasonable steps to maintain and improve the Service, but we cannot guarantee that every technical issue can be prevented.",
    ],
  },
  {
    heading: "12. Third-Party Services",
    p: [
      "The Service may rely on third-party infrastructure, hosting providers, processing tools, payment services or other integrations.",
      "We are not responsible for independent third-party websites or services that are outside our reasonable control. Your use of those services may be subject to separate terms and privacy policies.",
      "This clause does not exclude any responsibility that applicable law requires us to retain.",
    ],
  },
  {
    heading: "13. Disclaimer and Limitation of Liability",
    p: [
      "To the maximum extent permitted by law, we exclude any representation, warranty or guarantee that is not expressly required by applicable law or expressly provided by us.",
      "We do not guarantee that the Service will produce an exact reproduction of an original image or achieve a particular commercial, printing or design outcome.",
      "To the extent permitted by law, we are not responsible for losses arising from your misuse of the Service, your failure to retain copies of important files, or your use of processed outputs without appropriate quality checks.",
      "Nothing in these Terms excludes or limits liability for fraud, wilful misconduct, or any liability that cannot lawfully be excluded or limited.",
      "Where the law permits liability to be limited, our liability will be limited to the extent reasonably permitted by applicable law.",
      "Nothing in this section removes or reduces your rights under the Australian Consumer Law.",
    ],
  },
  {
    heading: "14. Indemnity",
    p: [
      "To the extent permitted by law, you are responsible for losses, claims and reasonable costs directly arising from your unlawful use of the Service or your infringement of a third party’s rights.",
      "This does not make you responsible for losses caused by our own unlawful conduct, negligence where liability cannot be excluded, or any other matter for which applicable law prevents us from transferring responsibility to you.",
    ],
  },
  {
    heading: "15. Privacy",
    p: [
      "Your use of the Service is also subject to our Privacy Policy.",
      "The Privacy Policy should explain what personal information we collect, how uploaded files and account information are handled, how long information is retained, whether information is shared with service providers, and how users can make privacy enquiries or complaints.",
      "We will handle personal information in accordance with applicable privacy laws.",
    ],
  },
  {
    heading: "16. Suspension and Termination",
    p: [
      "You may stop using the Service at any time.",
      "We may suspend or terminate access where reasonably necessary because of a material breach of these Terms, unlawful activity, security risks or circumstances affecting our ability to provide the Service.",
      "Where reasonably practicable, we will provide notice and an opportunity to address the issue before taking action, unless immediate action is necessary.",
      "Termination does not remove rights or obligations that arose before termination or rights that continue under applicable law.",
    ],
  },
  {
    heading: "17. Changes to These Terms",
    p: [
      "We may update these Terms to reflect changes to the Service, our business practices or applicable law.",
      "The latest version will be published on our website with its updated effective date.",
      "Where a change materially affects an existing paid arrangement or your legal rights, we will provide notice where required by law.",
      "Your continued use of the Service after updated Terms take effect constitutes acceptance only to the extent permitted by applicable law.",
    ],
  },
  {
    heading: "18. Governing Law",
    p: [
      "These Terms are governed by the laws of New South Wales, Australia, unless another applicable law requires otherwise.",
      "You and we submit to the jurisdiction of the courts of New South Wales, subject to any mandatory rights or protections available under applicable law.",
    ],
  },
  {
    heading: "19. Contact Us",
    p: ["For questions about these Terms, the Service, payments or your uploaded files, please contact:"],
    listAfter: 0,
    plainList: true,
    list: ["Business name: Get Set Design"],
  },
];

export default function Terms() {
  return (
    <LegalPage
      testId="terms-page"
      title="Terms & conditions"
      updated={UPDATED}
      sections={TERMS_SECTIONS}
      closing="By using Get Set Design, you acknowledge that you have read and understood these Terms and Conditions."
    />
  );
}
